import { useLocalizationContext } from '@va/localization';
import { ChartContext } from '@va/types/charts';
import { externalChartTooltip, LineChart, LoadingLogo, Paragraph } from '@va/ui/design-system';
import { buildChartDataset, formatLineDiagramDateLabels } from '@va/util/helpers';
import { ChartData } from 'chart.js';
import { memo, useCallback, useMemo } from 'react';
import { WebsiteActivityData } from './apiClient';

type VisitorChartProps = {
  data: WebsiteActivityData | undefined;
  inProgress: boolean;
  isError?: boolean;
  hiddenChartDatasets: WebsiteChartDataset[];
  websiteUrl: string;
};

export enum WebsiteChartDataset {
  Visits = 'visits',
  Sessions = 'sessions',
  NewVisitors = 'newVisitors',
  Visitors = 'visitors',
  ConvertedSessions = 'convertedSessions',
}

const VisitorsChart = ({ data, inProgress, isError, websiteUrl, hiddenChartDatasets }: VisitorChartProps) => {
  const { translate, locale } = useLocalizationContext();

  const tooltipFn = useCallback(
    (context: ChartContext, data?: WebsiteActivityData) => {
      if (!context || !data) return null;

      return externalChartTooltip(context, {
        currentTimeIntervals: data.timeIntervals,
        previousTimeIntervals: data.timeIntervals,
        showPrevious: false,
        title: websiteUrl,
      });
    },
    [websiteUrl],
  );

  const mappedData = useMemo(() => {
    if (!data) return null;

    return {
      labels: data.chartLabels ? formatLineDiagramDateLabels(data.chartLabels, locale) : [],
      datasets: Object.values(data.items).map((dataset) => {
        return buildChartDataset({
          dataset,
          hiddenDatasets: hiddenChartDatasets,
          isCurrent: true,
          isPreviousEnabled: false,
        });
      }),
    } satisfies ChartData<'line'>;
  }, [data, locale, hiddenChartDatasets]);

  if (inProgress)
    return (
      <div className='relative h-full w-full'>
        <LoadingLogo />
      </div>
    );

  if (isError)
    return (
      <div className='h-full w-full flex justify-center items-center'>{translate('notifications.generalError')}</div>
    );

  if (!mappedData)
    return (
      <div className='w-full h-full flex justify-center items-center'>
        <Paragraph>{translate('table.noData')}</Paragraph>
      </div>
    );

  return (
    <LineChart
      id={`website-activity-chart-${websiteUrl}`}
      chartData={mappedData}
      className='h-full'
      tooltipFn={(context) => tooltipFn(context, data)}
    />
  );
};

export default memo(VisitorsChart);
